import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  createAppSetting,
  getAppSettingById,
  updateAppSetting,
  clearCurrentSetting,
} from '../../redux/slices/appSettingSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import alertify from 'alertifyjs';

/**
 * AppSettingEditorPage - Admin page for creating/editing app settings
 *
 * Features:
 * - Form for key, description, value
 * - Key validation: auto lowercase, trim, pattern check
 * - Save/Update functionality
 * - Permission checks
 */
const AppSettingEditorPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const isEditMode = !!id;

  const { currentSetting, loading, error } = useSelector(
    (state) => state.appSettings
  );

  const hasViewPermission = useHasPermission('admin.app-settings.view');
  const hasCreatePermission = useHasPermission('admin.app-settings.create');
  const hasEditPermission = useHasPermission('admin.app-settings.edit');

  const [settingState, setSettingState] = useState({
    key: '',
    description: '',
    value: '',
  });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (isEditMode && hasViewPermission) {
      dispatch(getAppSettingById(id));
    } else if (!isEditMode) {
      dispatch(clearCurrentSetting());
    }
  }, [id, isEditMode, dispatch, hasViewPermission]);

  useEffect(() => {
    if (isEditMode && currentSetting && currentSetting._id === id) {
      setSettingState({
        key: currentSetting.key,
        description: currentSetting.description,
        value: currentSetting.value,
      });
    }
  }, [isEditMode, currentSetting, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for key field: lowercase and trim
    if (name === 'key') {
      // Convert to lowercase and remove spaces
      const processedValue = value.toLowerCase().trim().replace(/\s+/g, '_');
      // Only allow lowercase letters, numbers, and underscores
      const sanitizedValue = processedValue.replace(/[^a-z0-9_]/g, '');
      setSettingState((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setSettingState((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSave = async () => {
    if (!hasCreatePermission && !hasEditPermission) {
      alertify.error('Bu işlem için yetkiniz yok');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await dispatch(
          updateAppSetting({ id, settingData: settingState })
        ).unwrap();
        alertify.success('Sistem ayarı başarıyla güncellendi');
      } else {
        await dispatch(createAppSetting(settingState)).unwrap();
        alertify.success('Sistem ayarı başarıyla oluşturuldu');
      }
      setSnackbar({
        open: true,
        message: isEditMode
          ? 'Sistem ayarı başarıyla güncellendi'
          : 'Sistem ayarı başarıyla oluşturuldu',
        severity: 'success',
      });
      setTimeout(() => {
        history.push('/admin/app-settings');
      }, 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Ayar kaydedilemedi',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!hasViewPermission || (isEditMode && !hasEditPermission) || (!isEditMode && !hasCreatePermission)) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
            <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {isEditMode ? 'Sistem Ayarı Düzenle' : 'Yeni Sistem Ayarı Oluştur'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEditMode
                  ? 'Mevcut sistem ayarını düzenleyin'
                  : 'Yeni bir sistem ayarı oluşturun'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={loading || saving || (isEditMode && !hasEditPermission) || (!isEditMode && !hasCreatePermission)}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            label="Anahtar (Key)"
            name="key"
            value={settingState.key}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            disabled={isEditMode}
            error={!isEditMode && settingState.key.length > 0 && !/^[a-z0-9_]+$/.test(settingState.key)}
            helperText={
              isEditMode 
                ? "Anahtar düzenlenemez" 
                : settingState.key.length > 0 && !/^[a-z0-9_]+$/.test(settingState.key)
                  ? "Sadece küçük harf, rakam ve alt çizgi (_) kullanılabilir"
                  : "Benzersiz bir anahtar girin (örn: smtp_host). Otomatik olarak küçük harfe çevrilir."
            }
          />
          <TextField
            label="Açıklama"
            name="description"
            value={settingState.description}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            helperText="Bu ayarın ne için kullanıldığını açıklayın"
          />
          <TextField
            label="Değer"
            name="value"
            value={settingState.value}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
            multiline
            rows={4}
            helperText="Ayarın değerini girin (string, number, URL, token, liste vb. olabilir)"
          />
        </Paper>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AppSettingEditorPage;

